import ManualEntryRuleModel from "../ruleModels/ManualEntryRuleModel.js";
import Model from "../../Model.js";

/**
 * A scenario is a named collection of rules. An example of a scenario
 * could be "recruit Samantha" and "buy truck". A forecast usually has
 * a "baseline" scenario and many custom scenarios.
 *
 * @param {string} id - An identifier for the scenario, unique within
 *   the forecast template
 * @param {string} name - A short description of the scenario such as
 *   "best case" and "another pandemic"
 * @param {RuleTemplate[]} ruleTemplates - The prediction rules that should be
 *   activated when the scenario is activated. For a "recruit Samantha"
 *   scenario, there could be a rule to increase the company's salary
 *   costs, another rule to buy some furniture for Samantha and another
 *   rule, 3 months later, to increase sells by 20% (because we expect
 *   Samantha to sell the company's products)
 */
export default class Scenario extends Model {
	constructor({ id, name, ruleTemplates = [] } = {}) {
		super(...arguments);

		this._id = id;
		this._name = name;
		this._ruleTemplates = ruleTemplates;
	}

	getRuleTemplates() {
		return this._ruleTemplates;
	}

	addRuleTemplate(ruleTemplate) {
		this._ruleTemplates.push(ruleTemplate);
	}

	removeRuleTemplate(ruleTemplate) {
		this._ruleTemplates = this._ruleTemplates.filter(
			(rule) => rule.getId() !== ruleTemplate.getId(),
		);
	}

	setRuleTemplates(ruleTemplates) {
		this._ruleTemplates = ruleTemplates;
	}

	/**
	 * Return existing `RuleTemplate` for a specified cell and a rule model class.
	 *
	 * @param {string} rowId - The vertical position of the cell.
	 * @param {MonthPeriod} start - The horizontal position of the cell.
	 * @param {class} ruleModelClass - As many `RuleTemplate` can be set on a cell,
	 *   filter by this rule model class
	 */
	getOwnRuleTemplateFor({ rowId, start, ruleModelClass } = {}) {
		return this._ruleTemplates.find((ruleTemplate) =>
			ruleTemplate.match({ rowId, start, ruleModelClass }),
		);
	}

	getAllManualJournalEntryRuleTemplatesFor({ accountNumber, start }) {
		return this._ruleTemplates.filter((ruleTemplate) =>
			ruleTemplate.matchManualJournalEntry({ accountNumber, start }),
		);
	}

	getAllManualEntryRuleTemplatesFor({ rowId, start }) {
		return this._ruleTemplates.filter((ruleTemplate) =>
			ruleTemplate.match({
				rowId,
				start,
				ruleModelClass: ManualEntryRuleModel,
			}),
		);
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.id = this._id;
		properties.name = this._name;

		properties.ruleTemplateIds = this._ruleTemplates.map((ruleTemplate) =>
			ruleTemplate.getId(),
		);

		return properties;
	}
}
